import { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { imgUrl, API } from "@/lib/glossyApi";
import {
  RefreshCw, PenLine, Send, Image as ImageIcon, Instagram, Music2, ExternalLink, Lock, Clapperboard, Copy, Trash2,
} from "lucide-react";

function ReelBlock({ member }) {
  const [reels, setReels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [style, setStyle] = useState("scroll");
  const [picked, setPicked] = useState([]);
  const gallery = [
    ...(member.cover_image ? [{ id: "__cover", image: member.cover_image }] : []),
    ...([...(member.shot_gallery || [])].reverse()),
  ].filter((g, i, arr) => g.image && arr.findIndex((x) => x.image === g.image) === i);
  const maxPick = style === "scroll" ? 1 : 4;

  const togglePick = (img) => {
    setPicked((p) => p.includes(img) ? p.filter((x) => x !== img) : (p.length < maxPick ? [...p, img] : p));
  };

  const load = () => client.get(`/admin/videos?member_id=${member.id}`).then((r) => setReels(r.data.filter((v) => v.purpose === "article_reel"))).catch(() => {});
  useEffect(() => { load(); }, [member.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const processing = (reels || []).some((v) => v.status === "processing" || v.ig_status === "publishing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [processing]); // eslint-disable-line react-hooks/exhaustive-deps

  const make = async () => {
    setBusy(true);
    try {
      await client.post(`/admin/members/${member.id}/make-reel`, { style, image_keys: picked });
      toast.success("The studio is cutting their article reel — ready to download in a minute or two.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reel failed to start."); }
    setBusy(false);
  };
  const copyCap = (v) => {
    const c = v.caption || {};
    navigator.clipboard.writeText([c.hook, c.caption, (c.hashtags || []).join(" ")].filter(Boolean).join("\n\n"))
      .then(() => toast.success("Caption copied — paste straight into Instagram or TikTok."));
  };
  const postReel = async (v) => {
    const c = v.caption || {};
    const capText = [c.hook, c.caption, (c.hashtags || []).join(" ")].filter(Boolean).join("\n\n");
    const preview = capText ? `\n\nCaption:\n${capText.slice(0, 220)}${capText.length > 220 ? "…" : ""}` : "";
    const tagNote = member.instagram ? " It tags their handle and sends a collaborate invite." : "";
    if (!window.confirm(`Post this reel to Instagram now? It goes live immediately on the S/ME account.${tagNote}${preview}`)) return;
    try {
      const r = await client.post(`/admin/videos/${v.id}/instagram-publish`, { video_url: `${API}/files/${v.video_path}` });
      toast.success(r.data.auto_tagged
        ? `Reel on its way — @${r.data.auto_tagged} tagged with a collaborate invite. Live in a minute or two.`
        : "Reel on its way to Instagram — live in a minute or two.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reel post failed."); }
  };
  const delReel = async (v) => {
    if (!window.confirm("Delete this reel from the studio? Anything already posted to Instagram stays live.")) return;
    try { await client.delete(`/admin/videos/${v.id}`); toast.success("Reel deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div className="border border-ink/15 bg-white" data-testid="reel-block">
      <div className="p-4 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Clapperboard size={16} className="text-ink" />
          <p className="overline text-ink">Article reel · 9:16</p>
        </div>
        <button onClick={make} disabled={busy || processing}
          className="overline px-4 py-2 border border-ink hover:bg-ink hover:text-cream transition-colors disabled:opacity-50"
          data-testid="make-reel-btn">
          {processing ? "Cutting…" : "Make article reel (free)"}
        </button>
      </div>
      <div className="p-5 sm:p-6">
        <p className="font-sans text-sm text-ink/60 mb-4 max-w-2xl">
          A free vertical film cut by the studio. <b>Article scroll</b> pans down their real press page — masthead,
          headline, photo, opening lines, contact. <b>Photo film</b> plays their shots with the headline as captions.
          Pick the pictures below (click to choose{style === "scroll" ? " the hero shot" : ", in order, up to 4"}) — or leave blank and the studio picks.
          When it&apos;s cut, hit <b>Post reel</b> — it goes straight onto the S/ME Instagram, tagged to their handle with a collaborate invite.
        </p>
        <div className="flex gap-2 mb-4" data-testid="reel-style-picker">
          {[["scroll", "Article scroll"], ["montage", "Photo film"]].map(([k, lab]) => (
            <button key={k} onClick={() => { setStyle(k); setPicked([]); }}
              className={`overline px-4 py-2 border transition-colors ${style === k ? "bg-ink text-cream border-ink" : "border-ink/25 hover:border-ink"}`}
              data-testid={`reel-style-${k}`}>
              {lab}
            </button>
          ))}
        </div>
        {gallery.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-6" data-testid="reel-picture-picker">
            {gallery.slice(0, 14).map((g) => {
              const idx = picked.indexOf(g.image);
              return (
                <button key={g.image} onClick={() => togglePick(g.image)}
                  className={`relative w-32 h-40 bg-ink/5 overflow-hidden border-2 transition-colors ${idx >= 0 ? "border-gloss-gold" : "border-ink/10 hover:border-ink/40"}`}
                  data-testid={`reel-pick-${g.id || g.image}`}>
                  <img src={imgUrl({ cover_image: g.image })} alt="" className="w-full h-full object-contain" loading="lazy" />
                  {idx >= 0 && <span className="absolute top-1 left-1 bg-gloss-gold text-ink font-mono text-[10px] px-1.5 py-0.5">{idx + 1}</span>}
                </button>
              );
            })}
          </div>
        )}
        {reels?.length === 0 && <p className="font-mono text-[11px] uppercase tracking-widest text-ink/35">No reels yet.</p>}
        <div className="flex flex-wrap gap-5">
          {(reels || []).map((v) => (
            <div key={v.id} className="w-44" data-testid={`reel-${v.id}`}>
              {v.status === "done" ? (
                <video src={`${API}/files/${v.video_path}`} controls muted className="w-full aspect-[9/16] object-cover bg-ink" />
              ) : (
                <div className="w-full aspect-[9/16] bg-secondary border border-ink/15 flex items-center justify-center px-3 text-center">
                  <p className={`font-mono text-[10px] uppercase tracking-widest ${v.status === "error" ? "text-red-600" : "text-ink/40 animate-pulse"}`}>
                    {v.status === "error" ? "Didn't cut" : (Date.now() - new Date(v.created_at).getTime() > 12 * 60 * 1000 ? "Taking too long — delete & re-cut" : "Cutting…")}
                  </p>
                </div>
              )}
              {v.status === "error" && (
                <p className="font-mono text-[9px] text-red-600 mt-1.5 leading-snug" data-testid={`reel-error-${v.id}`}>{v.error || "Something interrupted the cut."} </p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {v.status === "done" && v.ig_status !== "posted" && (
                  <button onClick={() => postReel(v)} disabled={v.ig_status === "publishing"}
                    className="overline text-[10px] px-2.5 py-1.5 bg-gloss-gold text-ink hover:bg-ink hover:text-cream transition-colors disabled:opacity-50 flex items-center gap-1"
                    data-testid={`reel-post-ig-${v.id}`}>
                    <Send size={11} /> {v.ig_status === "publishing" ? "Posting…" : v.ig_status === "failed" ? "Retry post" : "Post reel"}
                  </button>
                )}
                {v.ig_status === "posted" && (
                  v.ig_permalink
                    ? <a href={v.ig_permalink} target="_blank" rel="noreferrer" className="overline text-[10px] px-2.5 py-1.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors" data-testid={`reel-ig-live-${v.id}`}>On Instagram ✓</a>
                    : <span className="overline text-[10px] px-2.5 py-1.5 bg-ink text-cream" data-testid={`reel-ig-live-${v.id}`}>On Instagram ✓</span>
                )}
                {v.status === "done" && (
                  <a href={`${API}/files/${v.video_path}?download=1`} className="overline text-[10px] px-2.5 py-1.5 bg-secondary hover:bg-gloss-gold/30 transition-colors" data-testid={`reel-download-${v.id}`}>Download ↓</a>
                )}
                {v.caption && (
                  <button onClick={() => copyCap(v)} className="overline text-[10px] px-2.5 py-1.5 bg-secondary hover:bg-gloss-gold/30 transition-colors flex items-center gap-1"><Copy size={11} /> Caption</button>
                )}
                <button onClick={() => delReel(v)} className="overline text-[10px] px-2 py-1.5 border border-ink/20 text-ink/50 hover:border-red-500 hover:text-red-600 transition-colors flex items-center gap-1"
                  data-testid={`reel-delete-${v.id}`} title="Delete this reel">
                  <Trash2 size={11} />
                </button>
              </div>
              {v.ig_status === "failed" && v.ig_error && (
                <p className="font-mono text-[9px] text-red-600 mt-1.5 leading-snug" data-testid={`reel-ig-error-${v.id}`}>{v.ig_error}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Social — Instagram (live) + TikTok (coming soon).
 * The feed tile is ALWAYS built from the Cover Member portrait (locked, free Pillow rebuild).
 */
export default function SocialSection({ member, setMember, onChange }) {
  const m = member;
  const soc = (m.package && m.package.social) || {};
  const [caption, setCaption] = useState(soc.caption || "");
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);

  const draftCaption = async () => {
    setBusy("draft");
    try {
      const r = await client.post(`/admin/social/draft-copy`, { member_id: m.id, kind: "instagram_feed" });
      setCaption(r.data.caption || r.data.body || "");
      setDirty(true);
      toast.success("Caption drafted in the S/ME voice.");
    } catch (e) { toast.error(e.response?.data?.detail || "Draft failed."); }
    setBusy("");
  };

  const rebuildTile = async () => {
    if (!m.directory_image) { toast.error("Generate (or swap in) the Cover Member portrait first — the tiles are built from it."); return; }
    setBusy("image");
    try {
      const r = await client.post(`/admin/social/tiles`, { member_id: m.id, kind: "feed" });
      const img = r.data.image || r.data.tile;
      const story = r.data.story || r.data.vertical;
      setMember({ ...m, package: { ...(m.package || {}), social: { ...soc, tile: img, story_tile: story } } });
      toast.success("Feed tile + Story rebuilt from the Cover Member portrait — no credits spent.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Tile rebuild failed."); }
    setBusy("");
  };

  const postStory = async () => {
    if (!soc.story_tile) { toast.error("Rebuild the tiles first."); return; }
    if (!window.confirm("Post this Story to Instagram now? Goes live immediately on the connected account.")) return;
    setBusy("story");
    try {
      await client.post(`/admin/social/instagram-publish-story`, { image_url: imgUrl({ cover_image: soc.story_tile }) });
      toast.success("Story posted live to Instagram.");
    } catch (e) { toast.error(e.response?.data?.detail || "Story post failed — check the connection in Social."); }
    setBusy("");
  };

  const postToInstagram = async () => {
    if (!soc.tile) { toast.error("Build the tile first."); return; }
    if (!caption.trim()) { toast.error("Write a caption first."); return; }
    if (!window.confirm("Post this to Instagram now? Goes live immediately on the connected account.")) return;
    setBusy("post");
    try {
      const r = await client.post(`/admin/social/instagram-publish`, { member_id: m.id, image_url: imgUrl({ cover_image: soc.tile }), caption });
      const id = r.data.media_id || r.data.id || r.data.post_id;
      setMember({ ...m, package: { ...(m.package || {}), social: { ...soc, caption, last_ig_post_id: id } } });
      setDirty(false);
      toast.success(r.data.auto_tagged ? `Posted live — @${r.data.auto_tagged} tagged with a collaborate invite.` : "Posted live to Instagram.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Instagram post failed — check the connection in Social."); }
    setBusy("");
  };

  return (
    <section id="section-social" data-testid="social-section" className="space-y-4 pt-4 scroll-mt-24">
      <div className="bg-ink text-cream px-5 py-3 grain">
        <span className="overline text-gloss-gold">Social · Instagram + TikTok</span>
      </div>

      {/* Instagram */}
      <div className="border border-ink/15 bg-white" data-testid="social-instagram-card">
        <div className="p-4 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Instagram size={16} className="text-ink" />
            <p className="overline text-ink">Instagram · live</p>
          </div>
          <a href="/studio#social" className="overline text-[10px] text-ink/60 flex items-center gap-1 hover:text-ink transition-colors" data-testid="social-open-page">
            Open Social page for calendar / scheduling <ExternalLink size={11} />
          </a>
        </div>
        <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div>
            <p className="overline text-ink/60 mb-2 flex items-center gap-1.5"><Lock size={11} /> Feed tile + Story · always the Cover Member</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="relative aspect-[4/5] bg-ink border border-ink/15 overflow-hidden" data-testid="social-ig-tile">
                  {soc.tile ? (
                    <img src={imgUrl({ cover_image: soc.tile })} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-cream/40">
                      <ImageIcon size={22} /> <p className="overline text-[10px]">no tile yet</p>
                    </div>
                  )}
                  {busy === "image" && (
                    <div className="absolute inset-0 bg-ink/80 flex items-center justify-center text-cream">
                      <RefreshCw size={20} className="animate-spin" />
                    </div>
                  )}
                </div>
                <p className="overline text-[9px] text-ink/50 mt-1 text-center">Feed · 1080×1350</p>
              </div>
              <div>
                <div className="relative aspect-[9/16] bg-ink border border-ink/15 overflow-hidden" data-testid="social-ig-story-tile">
                  {soc.story_tile ? (
                    <img src={imgUrl({ cover_image: soc.story_tile })} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-cream/40">
                      <ImageIcon size={22} /> <p className="overline text-[10px]">rebuild to create</p>
                    </div>
                  )}
                  {busy === "image" && (
                    <div className="absolute inset-0 bg-ink/80 flex items-center justify-center text-cream">
                      <RefreshCw size={20} className="animate-spin" />
                    </div>
                  )}
                </div>
                <p className="overline text-[9px] text-ink/50 mt-1 text-center">Story · 1080×1920</p>
              </div>
            </div>
            <button onClick={rebuildTile} disabled={!!busy || !m.directory_image}
              className="mt-3 w-full bg-ink text-cream px-4 py-3 overline flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-40" data-testid="social-ig-rebuild-tile">
              {busy === "image" ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rebuild both from Cover Member (free)
            </button>
            {soc.story_tile && (
              <button onClick={postStory} disabled={!!busy}
                className="mt-2 w-full border border-ink px-4 py-2.5 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="social-ig-post-story">
                {busy === "story" ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} Post Story now
              </button>
            )}
            <p className="font-mono text-[10px] text-ink/45 mt-2">Locked to the Cover Member portrait — swap the portrait in the Cover Member panel, then rebuild here. S/ME template, no AI credits.</p>
          </div>
          <div className="space-y-3">
            <button onClick={draftCaption} disabled={!!busy}
              className="w-full bg-ink text-cream px-4 py-2.5 overline flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="social-ig-draft-caption">
              {busy === "draft" ? <RefreshCw size={14} className="animate-spin" /> : <PenLine size={14} />} Draft caption with AI
            </button>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Caption (with hashtags)</label>
              <textarea value={caption} onChange={(e) => { setCaption(e.target.value); setDirty(true); }} rows={10}
                placeholder="The caption in real UK English, with a call-to-action and hashtags at the end."
                className="w-full border border-ink/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-ink" data-testid="social-ig-caption" />
              <p className="font-mono text-[9px] text-ink/40 mt-1">{caption.length} chars</p>
            </div>
            <button onClick={postToInstagram} disabled={!!busy || !soc.tile || !caption.trim()}
              className="w-full bg-gloss-gold text-ink px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="social-ig-post-now">
              {busy === "post" ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} Post to Instagram now
            </button>
            <p className="font-mono text-[10px] text-ink/45" data-testid="social-collab-note">
              {m.instagram
                ? `Posting automatically tags @${(m.instagram || "").replace(/^@/, "").split("/").filter(Boolean).pop()} and sends an Instagram collaborate invite — accept it and the post lands on their grid too.`
                : "Add their Instagram handle in Master → Social handles and every post will tag them + send a collaborate invite automatically."}
            </p>
            {dirty && <p className="font-mono text-[10px] text-gloss-gold text-center">Caption not saved — post to Instagram or move to Social page to schedule.</p>}
          </div>
        </div>
      </div>

      {/* Article reel — studio-rendered, free */}
      <ReelBlock member={m} />

      {/* TikTok — Coming soon */}
      <div className="border border-ink/15 bg-white opacity-70" data-testid="social-tiktok-card">
        <div className="p-4 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Music2 size={16} className="text-ink" />
            <p className="overline text-ink">TikTok</p>
          </div>
          <span className="overline text-[10px] text-gloss-gold" data-testid="social-tiktok-status">Coming soon</span>
        </div>
        <div className="p-5 sm:p-6 flex items-start gap-4">
          <div className="w-32 aspect-[9/16] bg-secondary border border-ink/15 flex items-center justify-center shrink-0">
            <Music2 size={22} className="text-ink/25" />
          </div>
          <div className="min-w-0">
            <p className="font-serif text-lg text-ink">TikTok integration is in the pipeline.</p>
            <p className="font-sans text-sm text-ink/60 mt-2">Draft your TikTok hook and caption here now so it&apos;s ready for the moment the API opens up. Post-out is disabled until then.</p>
            <textarea disabled placeholder="TikTok hook + caption (coming soon)…"
              className="w-full mt-3 border border-ink/20 bg-ink/5 px-3 py-2 font-sans text-sm text-ink/40" rows={3} data-testid="social-tiktok-caption" />
          </div>
        </div>
      </div>
    </section>
  );
}
